<template>
    <input
        ref="input"
        v-maska="mask"
        class="form-control"
        :value="modelValue"
        :placeholder="placeholder ? placeholder : $t('labels.empty')"
        @input="handleInput"
        @keydown="handleKeyDown"
        v-click-outside="hide"
    />
    <Teleport to="body">
        <span v-if="showed"
            @click.stop
            :class="{
                'select2-container select2-container--default': true,
                'select2-container--open': showed,
            }"
            :style="{
                position: 'absolute',
                top: top,
                left: left
            }"
        >
        <span ref="options" class="select2-dropdown select2-dropdown--above" :style="{width: width, 'z-index': zIndex}">
            <span class="select2-results">
                <ul class="select2-results__options" aria-expanded="true" aria-hidden="false" ref="list">
                    <li
                        :class="{
                            'select2-results__option': true,
                            'select2-results__option--highlighted': index === highlightedIndex
                        }"
                        v-for="(option, index) in options"
                        :key="index"
                        @mouseover="highlightedIndex = index"
                        @click.stop="selectOption(option)"
                    >
                        <slot name="optionItem" :option="option" :index="index">
                            {{ findByPath(option, optionValue) }}
                        </slot>
                    </li>
                    <li v-if="!isFetching && options.length === 0" class="select2-results__option select2-results__message">{{ $t('alerts.no_records_found') }}</li>
                    <li v-if="isFetching" class="select2-results__option" aria-disabled>Loading<span class="dots">.</span></li>
                </ul>
            </span>
        </span>
        </span>
    </Teleport>
</template>
<script>
import vClickOutside from 'click-outside-vue3'
import { vMaska } from "maska/vue"
import { httpClient } from "../../../../services/HttpClient";

export default {
    name: "InputAutoComplete",
    directives: {
        clickOutside: vClickOutside.directive,
        maska: vMaska
    },
    emits: ['update:modelValue', 'select'],
    props: {
        modelValue: {
            type: String,
            default: null
        },
        url: {
            type: String,
            required: true
        },
        optionValue: {
            type: String,
            default: 'name'
        },
        limit: {
            type: Number,
            default: 10
        },
        queryParams: {
            type: Object,
            default: () => ({})
        },
        isPhone: {
            type: Boolean,
            default: false
        },
        mask: {
            type: String,
            default: null
        },
        placeholder: {
            type: String,
            default: null
        },
        searchOption: {
            type: String,
            default: 'search'
        }
    },
    data() {
        return {
            showed: false,
            options: [],
            isFetching: false,
            page: 1,
            total: 0,
            search: '',
            debounceTimer: null,
            highlightedIndex: 0,
            top: '0px',
            left: '0px',
            width: '0px',
            zIndex: 950,
            position: 'bottom',
        }
    },
    methods: {
        handleInput(event) {
            if (document.activeElement !== event.target) return;

            let value = event.target.value;
            if (this.mask) {
                value = value.replace(/[^\p{L}\p{N}]/gu, '');
            }

            if (this.search === value) return;

            this.$emit('update:modelValue', value);
            this.search = value;
            this.debouncedFetch();
        },
        debouncedFetch() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.page = 1;
                this.options = [];
                this.fetchOptions();
            }, 500);
        },
        async fetchOptions() {
            if (this.isFetching || !this.search) return;
            if (this.total !== 0 && this.options.length >= this.total) return;

            this.isFetching = true;

            const params = {
                page: this.page,
                limit: this.limit,
                [this.searchOption]: this.isPhone ? this.search.replace(/[^\d]/g, '') : this.search,
                ...this.queryParams
            };

            try {
                const response = await httpClient.get(this.url, params);

                if (this.page === 1) {
                    this.options = response.data.data;
                } else {
                    this.options.push(...response.data.data);
                }
                this.total = response.data.meta.total;
                
                if (this.showed) {
                    this.$nextTick(() => {
                        this.calculatePosition();
                    });
                } else {
                    this.show();
                }
            } finally {
                this.isFetching = false;
            }
        },
        show() {
            if (this.showed) return;
            this.showed = true;

            const modalCount = document.getElementsByClassName('modal-backdrop').length
            if (modalCount > 0) {
                this.zIndex = 1000 + (modalCount * 10) + 7
            }

            this.$nextTick(() => {
                this.calculatePosition();
                this.addReachEndOnScrollListener();
                window.addEventListener('resize', this.calculatePosition);
                window.addEventListener('scroll', this.calculatePosition, true);
            });
        },
        hide() {
            if (!this.showed) return;
            this.showed = false;
            this.highlightedIndex = 0;
            this.removeReachEndOnScrollListener();
            window.removeEventListener('resize', this.calculatePosition);
            window.removeEventListener('scroll', this.calculatePosition, true);
        },
        calculatePosition() {
            if (!this.$refs.input || !this.showed) return;

            const inputRect = this.$refs.input.getBoundingClientRect();

            this.top = (inputRect.bottom + window.scrollY) + 'px';
            this.left = inputRect.left + 'px';
            this.width = inputRect.width + 'px';
        },
        handleKeyDown(event) {
            if (!this.showed) return;

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (this.highlightedIndex < this.options.length - 1) {
                    this.highlightedIndex++;
                    this.scrollToHighlighted();
                }
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (this.highlightedIndex > 0) {
                    this.highlightedIndex--;
                    this.scrollToHighlighted();
                }
            } else if (event.key === 'Enter') {
                if (this.options[this.highlightedIndex]) {
                    event.preventDefault();
                    this.selectOption(this.options[this.highlightedIndex]);
                }
            } else if (event.key === 'Escape') {
                this.hide();
            }
        },
        scrollToHighlighted() {
            this.$nextTick(() => {
                if (!this.$refs.list) return;
                const item = this.$refs.list.children[this.highlightedIndex];
                if (item) {
                    item.scrollIntoView({ block: 'nearest' });
                }
            });
        },
        selectOption(option) {
            const value = this.findByPath(option, this.optionValue);
            this.$emit('update:modelValue', value);
            this.$emit('select', option);
            this.search = value;
            this.hide();
        },
        findByPath(object, path) {
            return path.split('.').reduce((obj, key) => (obj ? obj[key] : null), object);
        },
        addReachEndOnScrollListener() {
            if (!this.$refs.list) return;
            this.listScrollHandler = (event) => {
                const el = event.target;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                    if (!this.isFetching && this.options.length < this.total) {
                        this.page++;
                        this.fetchOptions();
                    }
                }
            };
            this.$refs.list.addEventListener('scroll', this.listScrollHandler);
        },
        removeReachEndOnScrollListener() {
            if (this.$refs.list && this.listScrollHandler) {
                this.$refs.list.removeEventListener('scroll', this.listScrollHandler);
                this.listScrollHandler = null;
            }
        }
    },
    beforeUnmount() {
        this.hide();
    }
}
</script>
<style scoped>
.select2-results__option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>